import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  Channel,
  channelApiClient,
  ChannelForm,
  ChannelUpdateErrorResponse,
} from '@src/api/shared/community/channelApiClient';
import {
  ChannelMember,
  channelMemberApiClient,
} from '@src/api/shared/community/channelMemberApiClient';
import { Pagination } from '@src/api/types/pagination';
import defaultUserIcon from '@src/assets/images/avatar/default.png';
import CreateEditChannelModal from '@src/components/CreateEditChannelModal';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { TENANT_ROLE } from '@src/enum/tenantRoleEnum';
import CommunityChannelHeader from '@src/features/shared/community/components/CommunityChannelHeader';
import { ChannelVisibility } from '@src/features/shared/community/enums/channelVisibility';
import { useToast } from '@src/features/toast/context/toastContext';
import { useQueryParams } from '@src/hooks/useQueryParams';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { SHARED_URL } from '@src/routes/shared';
import { RootState } from '@src/store/store.ts';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import MoreVertMenu from '@src/ui/elements/table/MoreVertMenu';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

const CommunityMemberListForm: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [channel, setChannel] = useState<Channel>();
  const [channelMembers, setChannelMembers] = useState<ChannelMember[]>([]);
  const [isOnlyOneTenantAdmin, setIsOnlyOneTenantAdmin] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>();
  const navigate = useNavigate();
  const [isOpenDeleteMemberModal, setIsOpenDeleteMemberModal] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(false);
  const isFileDownloading = useSelector((state: RootState) => state.fileDownload.isFileDownloading);

  const menuRef = useRef<HTMLDivElement>(null);
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const [openChannelSettingModal, setOpenChannelSettingModal] = useState<boolean>(false);
  const [openSettingMenu, setOpenSettingMenu] = useState<boolean>(false);
  const [errors, setErrors] = useState<ChannelUpdateErrorResponse | null>(null);

  const { profile } = useSharedAuth();
  const isLoggedIn = profile?.tenantProfile?.isLogin || profile?.companyProfile?.isLogin;
  const isTenantAdmin = profile?.tenantProfile?.user?.isAdmin || false;

  const toast = useToast();

  const {
    searchValue,
    setSearchValue,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    getSearchParams,
    page,
    keyword,
  } = useQueryParams({});

  const currentUserType = profile?.isTenant
    ? ChannelMemberUserTypeEnum.TENANT_USER
    : ChannelMemberUserTypeEnum.COMPANY_USER;
  const currentUserId = profile?.isTenant
    ? profile?.tenantProfile?.user?.id
    : profile?.companyProfile?.user?.id;

  const isTenantAdminUser = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
  const isNonPublicChannel =
    channel?.visibility === ChannelVisibility.LIMITED ||
    channel?.visibility === ChannelVisibility.PRIVATE;

  const openDeleteMemberModal = (id?: number) => {
    if (!id) {
      return;
    }
    setSelectedMemberId(id);
    setIsOpenDeleteMemberModal(true);
  };
  const closeDeleteMemberModal = () => {
    setIsOpenDeleteMemberModal(false);
  };
  const confirmDeleteMember = async () => {
    if (!code || !selectedMemberId) {
      return;
    }
    setIsDeleteButtonDisabled(true);
    const res = await channelMemberApiClient.delete(code, selectedMemberId);
    handleApiResponse(res, () => {
      setIsOpenDeleteMemberModal(false);
      setIsDeleteButtonDisabled(false);
      if (isCurrentUser && !isTenantAdminUser) {
        navigate(SHARED_URL.COMMUNITY_DEFAULT_CHANNEL);
        return;
      }
      loadChannelMembers();
    });
  };

  const loadChannel = async () => {
    if (!code) {
      return;
    }
    const res = await channelApiClient.show(
      code,
      isTenantAdminUser ? { force_show: true } : undefined,
    );
    handleApiResponse(res, (data) => {
      setChannel(data.channel);
    });
  };

  const loadChannelMembers = async () => {
    if (!code || isFileDownloading) {
      return;
    }

    setIsLoading(true);
    const params = {
      ...getSearchParams(),
      exclude_tenant_members: currentUserType === ChannelMemberUserTypeEnum.COMPANY_USER,
    };
    const res = await channelMemberApiClient.index(code, params);
    handleApiResponse(
      res,
      (data) => {
        setChannelMembers(data.data.members);
        setPagination(data.pagination);

        const count = data.data.tenant_admin_users_count ?? 0;
        setIsOnlyOneTenantAdmin(count === 1);
      },
      undefined,
      () => {
        toast(TOAST_MESSAGE.LOAD_FAILED);
      },
    );
    setIsLoading(false);
  };

  const handleSettingMenuClick = () => {
    setOpenSettingMenu(true);
  };

  const handleChannelSettingOpenButtonClick = () => {
    setOpenSettingMenu(false);
    setOpenChannelSettingModal(true);
  };

  const handleChannelSettingModalClose = () => {
    setErrors(null);
    setOpenChannelSettingModal(false);
  };

  const handleLinkClick = (_e: React.MouseEvent<HTMLElement>, to: string) => {
    navigate(to);
  };

  const handleDetailClick = (channelMember: ChannelMember) => () => {
    if (!code || !channel) {
      return;
    }
    const url = getCommunityMembersShowUrl(channelMember);
    if (!url) {
      return;
    }
    navigate(
      generatePath(url, {
        code,
        id: channelMember.id,
      }),
    );
  };

  const getCommunityMembersShowUrl = (channelMember: ChannelMember) => {
    if (!code || !channel) {
      return undefined;
    }
    return channelMember.user_type === ChannelMemberUserTypeEnum.TENANT_USER
      ? SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_TENANT
      : SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_COMPANY;
  };

  const getDeletePermission = (channelMember: ChannelMember) => {
    const isCurrentUser =
      channelMember.user_type === currentUserType && channelMember.id === currentUserId;
    const isTargetTenantAdmin = channelMember.role.code === TENANT_ROLE.ADMIN;
    const isLastTenantAdmin = isTargetTenantAdmin && isOnlyOneTenantAdmin;

    // テナント管理者権限で削除可能かどうか
    const canTenantAdminDelete = isTenantAdminUser && !isLastTenantAdmin;
    // 一般ユーザーが自分自身を削除可能かどうか
    const canUserDeleteSelf = !isTenantAdminUser && isCurrentUser;
    // 削除メニューを表示するかどうか（非公開チャンネルで削除権限がある場合）
    const isKebabMenuVisible = isNonPublicChannel && (canTenantAdminDelete || canUserDeleteSelf);

    return { isCurrentUser, isKebabMenuVisible };
  };

  const onSave = async (formData: ChannelForm) => {
    if (!code) {
      return;
    }
    setErrors(null);
    const res = await channelApiClient.update(code, { channel: formData });
    handleApiResponse(
      res,
      async () => {
        setOpenChannelSettingModal(false);
        await loadChannel();
        toast(TOAST_MESSAGE.UPDATE);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  useEffect(() => {
    if (keyword) {
      setSearchValue(keyword);
    }
    (async () => {
      await loadChannel();
    })();
  }, []);

  useEffect(() => {
    loadChannelMembers();
  }, [keyword, page]);

  return (
    <>
      <SpinnerOverlay loading={isLoading} />
      <CommunityChannelHeader
        channel={channel || null}
        openSettingMenu={openSettingMenu}
        menuRef={menuRef}
        isTenantAdmin={isTenantAdmin}
        isLoggedIn={isLoggedIn || false}
        code={code}
        toggleMobileSidebar={toggleMobileSidebar}
        handleSettingMenuClick={handleSettingMenuClick}
        handleChannelSettingOpenButtonClick={handleChannelSettingOpenButtonClick}
        handleLinkClick={handleLinkClick}
        navigate={navigate}
        showDisplayNameBanner={false}
        isSticky={false}
        containerClassName='hidden lg:flex'
        innerContainerClassName='h-[60px]'
      />
      <main className='px-6 py-11 md:px-2 md:pb-8 md:pt-0'>
        <div className='mb-6 flex justify-between md:mb-0 md:flex-col'>
          <div className='flex flex-col gap-4 md:gap-6 md:px-2 md:pb-2 md:pt-8'>
            <div className='flex h-[42px] items-center md:h-10'>
              <h1 className='text-xl font-semibold'>チャンネル参加者一覧</h1>
            </div>
            <div className='flex h-[42px] items-center md:h-[26px]'>
              <p className='md:text-sm'>
                {keyword
                  ? `「${keyword}」で絞り込んだ結果：${pagination?.total ?? 0}`
                  : `参加人数：${pagination?.total ?? 0}`}
              </p>
            </div>
          </div>
          <div className='flex flex-col items-end gap-4 md:-order-1 md:w-full md:flex-row md:items-center md:gap-2 md:py-2'>
            <div className='flex w-[250px] min-w-0 grow items-start rounded-[2px] [&>div]:w-full'>
              <InputSearch
                value={searchValue}
                defaultValue={keyword || ''}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={() => {
                  setSearchValue('');
                  setSearchParams(new URLSearchParams());
                }}
              />
            </div>
            {isTenantAdminUser && isNonPublicChannel && (
              <div className='w-auto min-w-[162px] md:shrink-0'>
                <Button
                  size='s'
                  variant='primary'
                  onClick={() => {
                    navigate(
                      generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_INVITATION, { code }),
                    );
                  }}
                >
                  チャンネルに招待
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className='overflow-auto md:overflow-hidden'>
          <table className='size-full min-w-[900px] table-fixed text-sm md:min-w-1'>
            <colgroup>
              <col className='w-[22%] md:w-[calc(100%-130px)]' />
              <col className='w-[22%] md:w-0' />
              <col className='w-[22%] md:w-0' />
              <col className='w-[17%] md:w-0' />
              <col className='w-[17%] md:w-0' />
              <col className='w-[162px] md:w-[146px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50 md:hidden'>
              <tr className='h-[42px]'>
                <th className='p-4 text-left text-xs font-semibold'>名前</th>
                <th className='p-4 text-left text-xs font-semibold'>企業名</th>
                <th className='p-4 text-left text-xs font-semibold'>所属</th>
                <th className='p-4 text-left text-xs font-semibold'>役職</th>
                <th className='p-4 text-left text-xs font-semibold'>業種</th>
                <th className='p-4 text-left text-xs font-semibold'></th>
                <th className='p-4 text-left text-xs font-semibold'></th>
              </tr>
            </thead>
            <tbody>
              {channelMembers.length > 0
                ? channelMembers.map((channelMember) => {
                    const { isCurrentUser, isKebabMenuVisible } =
                      getDeletePermission(channelMember);

                    const isTenantMember =
                      channelMember.user_type === ChannelMemberUserTypeEnum.TENANT_USER &&
                      channelMember.role?.code === TENANT_ROLE.MEMBER;

                    return (
                      <tr
                        className={` [&:nth-child(2n)]:bg-gray-10 ${isTenantMember ? '!bg-gray-400' : ''}`}
                        key={`${channelMember.user_type}-${channelMember.id}`}
                      >
                        <td className='flex items-center gap-2 px-4 py-[18px]'>
                          <img
                            className='size-6 rounded-full object-contain'
                            src={channelMember.avatar_url || defaultUserIcon}
                            width={24}
                            height={24}
                            alt=''
                          />
                          <span className='truncate'>{channelMember.display_name}</span>
                        </td>
                        <td className='truncate px-4 py-[18px]'>{channelMember.company_name}</td>
                        <td className='truncate px-4 py-[18px]'>{channelMember.affiliation}</td>
                        <td className='truncate px-4 py-[18px]'>{channelMember.position}</td>
                        <td className='truncate px-4 py-[18px]'>{channelMember.industry}</td>
                        <td className='truncate py-2'>
                          <div className='flex items-center justify-end gap-6 px-2 md:gap-2'>
                            <div className='w-[76px] text-center'>
                              <Button
                                size='s'
                                outline
                                variant='quaternary'
                                onClick={handleDetailClick(channelMember)}
                              >
                                詳細
                              </Button>
                            </div>
                            <div className={'flex w-[38px] items-center justify-center'}>
                              <div className='grid h-full place-items-center'>
                                {isKebabMenuVisible && (
                                  <MoreVertMenu
                                    menuWidth={184}
                                    menuItems={[
                                      {
                                        label: 'チャンネルから削除',
                                        variant: 'warning',
                                        onClick: () => {
                                          setIsCurrentUser(isCurrentUser);
                                          openDeleteMemberModal(
                                            channelMember.member_id ?? undefined,
                                          );
                                        },
                                      },
                                    ]}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
          {!keyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
          {/* ページネーション */}
          {pagination && pagination.total !== 0 && (
            <div className='mt-6 lg:flex lg:justify-center'>
              <PaginationComponent
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
        {channel && (
          <CreateEditChannelModal
            isOpen={openChannelSettingModal}
            channel={channel}
            onSave={onSave}
            onClose={handleChannelSettingModalClose}
            errors={errors || undefined}
          />
        )}
        <ConfirmModal
          title='チャンネルから削除しますか？'
          message='この操作により、この参加者はチャンネルへアクセスできなくなりますが、よろしいですか？'
          confirmText='削除'
          cancelText='戻る'
          confirmDisabled={isDeleteButtonDisabled}
          isOpen={isOpenDeleteMemberModal}
          onConfirm={() => confirmDeleteMember()}
          onClose={closeDeleteMemberModal}
          isDanger={true}
        />
      </main>
    </>
  );
};

export default CommunityMemberListForm;
